import type { InstructionStepDto, SupportingMaterialDto } from './types';

export function sortInstructionSteps(steps: InstructionStepDto[]): InstructionStepDto[] {
    return [...steps].sort((a, b) => (a.e251_StepNumber ?? 0) - (b.e251_StepNumber ?? 0));
}

export function getEffectiveInstructionSteps(steps: InstructionStepDto[] | undefined): InstructionStepDto[] {
    if (!steps?.length) return [];
    return sortInstructionSteps(steps);
}

export function hasDetailedInstructionSteps(steps?: InstructionStepDto[]): boolean {
    return Boolean(steps?.some(s => (s.e251_DetailedInstructions ?? '').trim().length > 0));
}

export function getActiveStepIndex(steps: InstructionStepDto[]): number {
    const activeIdx = steps.findIndex(s => s.e251_IsActive);
    return activeIdx >= 0 ? activeIdx : 0;
}

export function getActiveStepNumber(steps: InstructionStepDto[]): number | null {
    const active = steps.find(s => s.e251_IsActive);
    return active?.e251_StepNumber ?? null;
}

const SECTION_HEADING_RE = /^##\s+Section\s+[A-Z](?:\s*\(step\s+\d+\))?\s*$/gim;
const STEP_IN_HEADING_RE = /step\s+(\d+)/i;

type ParsedWorksheetSection = {
    heading: string;
    body: string;
    stepNumber: number | null;
};

function getStepDisplayNumber(lessonSteps: InstructionStepDto[], stepId: string): number | null {
    const ordered = getEffectiveInstructionSteps(lessonSteps);
    for (let i = 0; i < ordered.length; i++) {
        if (String(ordered[i].e251_StepId) === stepId) {
            return i + 1;
        }
    }
    return null;
}

function findSectionIndexForStep(
    sections: ParsedWorksheetSection[],
    stepId: string,
    lessonSteps: InstructionStepDto[] | undefined,
    relatedStepIds: string[] | undefined
): number {
    const stepNumber = getStepDisplayNumber(lessonSteps ?? [], stepId);
    if (stepNumber != null) {
        const byNumber = sections.findIndex(s => s.stepNumber === stepNumber);
        if (byNumber >= 0) return byNumber;
    }

    if (relatedStepIds?.length) {
        const relatedIndex = relatedStepIds.findIndex(id => String(id) === String(stepId));
        if (relatedIndex >= 0 && relatedIndex < sections.length) return relatedIndex;
    }

    return -1;
}

export function parseWorksheetSections(content: string): { preamble: string; sections: ParsedWorksheetSection[] } {
    if (!content?.trim()) return { preamble: '', sections: [] };

    const matches = [...content.matchAll(SECTION_HEADING_RE)];
    if (matches.length === 0) return { preamble: content.trim(), sections: [] };

    const firstIndex = matches[0].index ?? 0;
    const preamble = content.slice(0, firstIndex).trim();
    const sections: ParsedWorksheetSection[] = [];

    for (let i = 0; i < matches.length; i++) {
        const headingStart = matches[i].index ?? 0;
        const bodyStart = headingStart + matches[i][0].length;
        const bodyEnd = i + 1 < matches.length ? (matches[i + 1].index ?? content.length) : content.length;
        const heading = content.slice(headingStart, bodyStart).trimEnd();
        const body = content.slice(bodyStart, bodyEnd).trim();
        const stepMatch = heading.match(STEP_IN_HEADING_RE);
        const stepNumber = stepMatch ? Number.parseInt(stepMatch[1], 10) : null;

        sections.push({
            heading,
            body,
            stepNumber: Number.isFinite(stepNumber) ? stepNumber : null
        });
    }

    return { preamble, sections };
}

function formatSectionHeadingForDisplay(heading: string): string {
    return heading.replace(/\s*\(step\s+\d+\)\s*$/i, '').trimEnd();
}

function buildWorksheetSectionContent(preamble: string, section: ParsedWorksheetSection): string {
    const parts: string[] = [];
    if (preamble.trim()) parts.push(preamble.trim());
    parts.push(formatSectionHeadingForDisplay(section.heading));
    if (section.body.trim()) parts.push('', section.body.trim());
    return parts.join('\n').trim();
}

function buildWorksheetSectionsOnlyContent(sections: ParsedWorksheetSection[]): string {
    return sections
        .map(section => buildWorksheetSectionContent('', section))
        .join('\n\n')
        .trim();
}

/** Remove adult/teacher preamble from worksheet markdown; leaves readings and unsliced worksheets unchanged. */
export function stripWorksheetPreamble(content: string): string {
    if (!content?.trim()) return content ?? '';

    const { preamble, sections } = parseWorksheetSections(content);
    if (sections.length === 0) return content;

    if (!preamble.trim()) return content.trim();

    return buildWorksheetSectionsOnlyContent(sections);
}

export function sliceWorksheetContentForStep(
    content: string,
    stepId: string | undefined,
    lessonSteps: InstructionStepDto[] | undefined,
    relatedStepIds?: string[]
): string | null {
    if (!content?.trim() || !stepId) return content ?? null;

    const { preamble, sections } = parseWorksheetSections(content);
    if (sections.length === 0) return content;

    const matchIndex = findSectionIndexForStep(sections, String(stepId), lessonSteps, relatedStepIds);
    if (matchIndex < 0) return null;

    const includePreamble = matchIndex === 0 && Boolean(preamble.trim());
    return buildWorksheetSectionContent(includePreamble ? preamble : '', sections[matchIndex]);
}

export type ResolveMaterialsOptions = {
    /** When true (default), worksheet bodies are sliced to the current step's section. */
    sliceWorksheets?: boolean;
};

export function stepHasNestedMaterials(step: InstructionStepDto): boolean {
    return Boolean(step.e251_Reading || step.e251_WorksheetSection || step.e251_Video);
}

function worksheetTitleFromSection(step: InstructionStepDto): string {
    const letter = step.e251_WorksheetSection?.e251_Letter?.trim();
    if (letter) return `Section ${letter}`;
    return step.e251_Summary?.trim() || 'Worksheet';
}

/** Stable id for nested step materials (worksheet/reading/video share one step id). */
export function virtualMaterialId(stepId: string | undefined, materialType: string): string | undefined {
    if (!stepId) return undefined;
    return `${stepId}:${materialType}`;
}

function sanitizeVideoDisplayContent(content: string | undefined, title?: string): string | undefined {
    if (!content?.trim()) return content;
    if (!/\bFig\.\s*[A-Z]\b/i.test(content)) return content;
    const trimmedTitle = title?.trim();
    if (trimmedTitle) {
        return `Watch this video about ${trimmedTitle} and pause at each main stage to discuss what you see.`;
    }
    return content.replace(/\bFig\.\s*[A-Z]\b/gi, 'the video');
}

/** Build virtual SupportingMaterialDto entries from a step's nested reading, worksheet, and video. */
export function buildVirtualMaterialsForStep(step: InstructionStepDto): SupportingMaterialDto[] {
    const materials: SupportingMaterialDto[] = [];
    const stepId = step.e251_StepId ? String(step.e251_StepId) : undefined;
    const stepNumber = step.e251_StepNumber ?? 0;
    const relatedStepIds = stepId ? [stepId] : undefined;
    const baseSort = stepNumber * 10;

    const worksheet = step.e251_WorksheetSection;
    if (worksheet?.e251_WorksheetContent?.trim()) {
        materials.push({
            e251_MaterialId: virtualMaterialId(stepId, '10'),
            e251_MaterialType: '10',
            e251_Title: worksheetTitleFromSection(step),
            e251_Content: worksheet.e251_WorksheetContent,
            e251_RelatedStepIds: relatedStepIds,
            e251_SortOrder: baseSort
        });
    }

    const reading = step.e251_Reading;
    if (reading?.e251_ReadingContent?.trim()) {
        materials.push({
            e251_MaterialId: virtualMaterialId(stepId, '20'),
            e251_MaterialType: '20',
            e251_Title: reading.e251_ReadingTitle?.trim() || step.e251_Summary?.trim() || 'Reading',
            e251_Content: reading.e251_ReadingContent,
            e251_RelatedStepIds: relatedStepIds,
            e251_SortOrder: baseSort + 1
        });
    }

    const video = step.e251_Video;
    if (video) {
        const hasSelectableClips = (video.e251_StepVideoOptions ?? []).some(option =>
            Boolean(option.e251_VideoId?.trim())
        );
        const hasUrl = Boolean(video.e251_StepVideoUrl?.trim());
        // Empty attach stubs (no clips, no URL) are treated as no video — omit them.
        // Removed-from-YouTube placeholders still show even with empty options.
        if (video.e251_VideoRemovedFromYouTube || hasSelectableClips || hasUrl) {
            const videoTitle = video.e251_StepVideoTitle?.trim() || step.e251_Summary?.trim() || 'Video';
            materials.push({
                e251_MaterialId: virtualMaterialId(stepId, '30'),
                e251_MaterialType: '30',
                e251_Title: videoTitle,
                e251_Content: sanitizeVideoDisplayContent(video.e251_StepVideoContent, videoTitle),
                e251_Url: video.e251_StepVideoUrl,
                e251_VideoChannelTitle: video.e251_StepVideoChannelTitle,
                e251_VideoOptions: video.e251_StepVideoOptions,
                e251_SelectedVideoId: video.e251_SelectedVideoId,
                e251_VideoApproved: video.e251_StepVideoApproved,
                e251_VideoRemovedFromYouTube: video.e251_VideoRemovedFromYouTube,
                e251_RelatedStepIds: relatedStepIds,
                e251_SortOrder: baseSort + 2
            });
        }
    }

    return materials;
}

export function collectVirtualMaterialsFromSteps(
    steps: InstructionStepDto[] | undefined
): SupportingMaterialDto[] {
    if (!steps?.length) return [];
    return getEffectiveInstructionSteps(steps).flatMap(buildVirtualMaterialsForStep);
}

export function lessonHasNestedStepMaterials(lesson: {
    e251_InstructionSteps?: InstructionStepDto[];
}): boolean {
    return (lesson.e251_InstructionSteps ?? []).some(stepHasNestedMaterials);
}

/** Virtual materials built from nested instruction-step children. */
export function resolveLessonMaterials(lesson: {
    e251_InstructionSteps?: InstructionStepDto[];
}): SupportingMaterialDto[] {
    return collectVirtualMaterialsFromSteps(lesson.e251_InstructionSteps);
}

export function findLessonMaterialById(
    lesson: {
        e251_InstructionSteps?: InstructionStepDto[];
    },
    materialId: string | undefined
): SupportingMaterialDto | undefined {
    if (!materialId) return undefined;
    const key = String(materialId);
    return resolveLessonMaterials(lesson).find(m => m.e251_MaterialId && String(m.e251_MaterialId) === key);
}

export function resolveMaterialsForStep(
    step: InstructionStepDto,
    _allMaterials?: SupportingMaterialDto[],
    _lessonSteps?: InstructionStepDto[],
    _options?: ResolveMaterialsOptions
): SupportingMaterialDto[] {
    if (!stepHasNestedMaterials(step)) return [];
    return buildVirtualMaterialsForStep(step);
}

export function getPrintableMaterials(materials: SupportingMaterialDto[]): SupportingMaterialDto[] {
    return materials.filter(
        m =>
            (m.e251_MaterialType === '10' || m.e251_MaterialType === '20') &&
            Boolean(m.e251_Content?.trim())
    );
}

export function materialStableKey(material: SupportingMaterialDto, index = 0): string {
    if (material.e251_MaterialId) return String(material.e251_MaterialId);
    const type = material.e251_MaterialType ?? 'unknown';
    const title = material.e251_Title ?? '';
    const sort = material.e251_SortOrder ?? index;
    return `${type}-${sort}-${title}`;
}

export function dedupeMaterialsById(materials: SupportingMaterialDto[]): SupportingMaterialDto[] {
    const seen = new Set<string>();
    const result: SupportingMaterialDto[] = [];
    for (const [index, material] of materials.entries()) {
        const id = materialStableKey(material, index);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        result.push(material);
    }
    return result;
}

/** Strip a leading "1.", "1)", "Step 1:", etc. from a step summary already shown beside Step N of M. */
export function stripLeadingStepNumberFromTitle(title?: string): string {
    if (!title?.trim()) return '';
    return title
        .trim()
        .replace(/^(?:step\s*)?\d+\s*[:.)\-–—]\s*/i, '')
        .trim();
}

function normalizeMaterialTitle(text: string): string {
    return text
        .trim()
        .replace(/^#+\s*/, '')
        .replace(/^\*\*(.+)\*\*$/, '$1')
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/\s+/g, ' ')
        .toLowerCase();
}

/** Remove a leading markdown heading or bold line when it repeats the material title already shown in the card header. */
export function stripDuplicateMaterialTitleFromContent(content: string, title?: string): string {
    if (!content?.trim() || !title?.trim()) return content;

    const normalizedTitle = normalizeMaterialTitle(title);
    const lines = content.split('\n');
    let index = 0;

    while (index < lines.length && !lines[index].trim()) index += 1;
    if (index >= lines.length) return content;

    const firstLine = lines[index].trim();
    if (/^##\s+Section\s+[A-Z]/i.test(firstLine)) return content;

    const headingMatch = firstLine.match(/^#{1,6}\s+(.+)$/);
    const boldMatch = firstLine.match(/^\*\*(.+)\*\*$/);
    const candidate = headingMatch?.[1] ?? boldMatch?.[1] ?? firstLine;

    if (normalizeMaterialTitle(candidate) !== normalizedTitle) return content;

    index += 1;
    while (index < lines.length && !lines[index].trim()) index += 1;
    return lines.slice(index).join('\n');
}

export function resolveStepNumbersForIds(
    relatedStepIds: string[] | undefined,
    lessonSteps: InstructionStepDto[] | undefined
): number[] {
    if (!relatedStepIds?.length) return [];
    const steps = lessonSteps ?? [];
    const numbers: number[] = [];
    for (const id of relatedStepIds) {
        if (!id) continue;
        const match = steps.find(s => String(s.e251_StepId) === String(id));
        if (match?.e251_StepNumber != null && !Number.isNaN(match.e251_StepNumber)) {
            numbers.push(match.e251_StepNumber);
        }
    }
    return [...new Set(numbers)].sort((a, b) => a - b);
}

export function formatStepBadgeFromIds(
    relatedStepIds: string[] | undefined,
    lessonSteps: InstructionStepDto[] | undefined
): string | null {
    const sorted = resolveStepNumbersForIds(relatedStepIds, lessonSteps);
    if (!sorted.length) return null;
    if (sorted.length === 1) return `Covers step ${sorted[0]}`;
    return `Covers steps ${sorted.join(', ')}`;
}
